// ─── src/utils/weddingJourney.js ──────────────────────────────────────────────
// "Our Journey": the append-only wedding_events log, read back as the couple's own
// notebook. PURE. The one rule (Taiwo's): render ONLY what is in the log. Never a
// "why" she did not state. Each line is a fact from an event payload, nothing more.
// The bride never sees "event", "scenario", or "narrative memory".

const cap = (s) => (s ? String(s).split(/\s+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : s);
// "Chose a intimate style." The couple's own notebook should not read like a template.
// Sound, not spelling: a style beginning with a vowel LETTER usually takes "an", but
// "a European feel" and "a unique style" do not, so those openings are excluded.
const article = (word) => {
  const w = String(word ?? '').trim().toLowerCase();
  if (!w) return 'a';
  // "yoo" openings take "a" despite the vowel: unique, usual, European. Kept to
  // explicit prefixes — a broad /^u[consonant]/ swallowed "understated" and
  // "unusual", which both take "an".
  if (/^(eu|uni|use|usu|uti|ubi|one)/.test(w)) return 'a';
  return /^[aeiou]/.test(w) ? 'an' : 'a';
};
const ROLE_WORD = { venue: 'venue', planner: 'planner', photographer: 'photographer', videographer: 'videographer', florist: 'florist', catering: 'caterer', entertainment: 'entertainment', celebrant: 'celebrant' };

// One event → one couple-facing sentence, or null to omit (Aura's internal steps,
// exploring placements, etc. are not part of HER story).
export function journeyLine(e) {
  const p = e?.payload || {};
  const type = e?.event_type;
  switch (type) {
    case 'wedding_minted':      return p?.dna?.destination ? `Began planning your wedding in ${cap(p.dna.destination)}.` : 'Began planning your wedding.';
    case 'dna_destination_set': return p.destination ? `Set your destination to ${cap(p.destination)}.` : null;
    case 'dna_guestCount_set':  return p.guestCount   ? `Settled on ${p.guestCount} guests.` : null;
    case 'dna_style_set':       return p.style        ? `Chose ${article(p.style)} ${String(p.style).toLowerCase()} style.` : null;
    case 'wedding_date_set':    return p.label        ? `Set your date: ${p.label}.` : (p.date ? 'Set your wedding date.' : null);
    case 'candidate_rejected':  return p.entity_name  ? `Set aside ${p.entity_name}.` : null;
    case 'scenario_forked':     return p.destination  ? `Explored ${cap(p.destination)}.` : null;
    case 'scenario_preserved':  return p.destination  ? `Kept ${cap(p.destination)} safe.` : null;
    case 'scenario_restored':   return p.destination  ? `Reopened your ${cap(p.destination)} wedding.` : 'Reopened an earlier idea.';
    default:
      if (typeof type === 'string' && type.startsWith('team_')) {
        const { state, entity_name: name, role } = p;
        if (!name) return null;
        if (state === 'shortlisted') return `Shortlisted ${name}${role ? ` as your ${ROLE_WORD[role] || role}` : ''}.`;
        if (state === 'enquired')    return `Reached out to ${name}.`;
        if (state === 'booked')      return `Booked ${name}.`;
        return null; // exploring / suggested / needed, Aura's steps, not hers
      }
      return null;
  }
}

// "5 Jul" style day label.
function dayLabel(iso) {
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); }
  catch { return ''; }
}

// Events (ascending) → chronological day-grouped notebook. Drops omitted events and
// collapses a line that repeats back-to-back within a day.
export function buildJourney(events) {
  const days = [];
  let cur = null;
  for (const e of (events || [])) {
    const line = journeyLine(e);
    if (!line) continue;
    const day = dayLabel(e.created_at);
    if (!cur || cur.day !== day) { cur = { day, dateISO: e.created_at, lines: [] }; days.push(cur); }
    if (cur.lines[cur.lines.length - 1] !== line) cur.lines.push(line);
  }
  return days;
}
